// src/components/admin/AdminPanel.jsx
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../shared/Header';
import KPICard from './KPICard';
import TabGroup from './TabGroup';
import SolicitudesTab from './SolicitudesTab';
import GestionAusencias from './GestionAusencias';
import LogsTab from './LogsTab';
import UsuariosTab from './UsuariosTab';
import AdminSaldos from './AdminSaldos';
import InformesPanel from './InformesPanel';
import { api, API_URL } from '../../services/api.js';

// Servicios de solicitudes
import { listarSolicitudes, aprobarSolicitud, rechazarSolicitud } from '../../services/solicitudes';

export default function AdminPanel({ session, onLogout }) {
  const navigate = useNavigate();

  const [solicitudes, setSolicitudes] = useState([]);
  const [ausencias, setAusencias] = useState([]);
  const [logs, setLogs] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosGestion, setUsuariosGestion] = useState([]);
  const [msg, setMsg] = useState('');
  const [activeTab, setActiveTab] = useState('Solicitudes');
  const [filtroInicialSolicitudes, setFiltroInicialSolicitudes] = useState(null);

  const token = session?.token;
  const user = session?.user;
  const role = (session?.role || '').toLowerCase();

  // 🔹 Rescate para branding del Header por si session.user es string
  const userObj = (typeof user === 'object' && user) ? user : {};
  const [me, setMe] = useState(null); // datos de /auth/me

  useEffect(() => {
    let cancel = false;
    async function loadMe() {
      // si ya tenemos brand/company, no hace falta pedir
      if (userObj?.brand_dir && userObj?.company_name) return;
      if (!token) return;
      try {
        const data = await api.get('/auth/me', token);
        if (!cancel && data?.user) setMe(data.user);
      } catch {
        // intenta /me como alternativa si tu backend lo expone
        try {
          if (cancel) return;
          const data2 = await api.get('/me', token);
          if (!cancel && data2?.user) setMe(data2.user);
        } catch { /* noop */ }
      }
    }
    loadMe();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userObj?.brand_dir, userObj?.company_name]);

  const brandForHeader = userObj?.brand_dir || me?.brand_dir || null;
  const companyForHeader = userObj?.company_name || me?.company_name || null;
  const pausaMin = Number(userObj?.pausa_comida_minutos ?? me?.pausa_comida_minutos ?? 0) || 0;
  const pausaEnabled = !!(userObj?.descuenta_pausa_en_resumen ?? me?.descuenta_pausa_en_resumen ?? false);

  if (!token || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600 font-semibold">
        Error: Sesión inválida. Por favor inicia sesión nuevamente.
      </div>
    );
  }

  // helper carga genérico
  const fetchData = async (url, setter, extraHeaders = {}) => {
    try {
      const data = await api.get(url, token, extraHeaders);
      setter(Array.isArray(data) ? data : data ?? []);
    } catch (err) {
      console.error(`❌ Error cargando ${url}:`, err);
      setter([]);
    }
  };

  // cargar listas
  const cargarSolicitudes = async () => {
    try {
      const data = await listarSolicitudes(token, {
        page: 1,
        per_page: 50,
        order_by: 'timestamp',
        order_dir: 'desc',
        solo_pendientes: false,
      });
      setSolicitudes(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error('❌ Error listando solicitudes (server):', err);
      setSolicitudes([]);
    }
  };
  const cargarAusencias = () => fetchData('/ausencias', setAusencias);
  const cargarLogs = () => fetchData('/logs', setLogs);
  const cargarUsuarios = () => fetchData('/usuarios', setUsuarios);
  const cargarUsuariosGestion = () => fetchData('/usuarios?incluir_inactivos=true', setUsuariosGestion);

  const resolver = async (id, aprobar) => {
    try {
      if (aprobar) await aprobarSolicitud(token, id);
      else await rechazarSolicitud(token, id, '');
      await cargarSolicitudes();
    } catch (err) {
      console.error('❌ Error resolviendo solicitud:', err);
      alert(`❌ Error resolviendo solicitud: ${err.message}`);
    }
  };

  const crearUsuario = async (payload) => {
    try {
      await api.post('/registrar', payload, token, { usuario: userObj?.email || user });
      setMsg('✅ Usuario creado correctamente');
      cargarLogs();
      cargarUsuarios();
      cargarUsuariosGestion();
    } catch (err) {
      console.error('❌ Error creando usuario:', err);
      setMsg('❌ ' + (err.message || 'Error de conexión al crear usuario'));
    }
  };

  const darBajaUsuario = async (id, fechaBaja = null) => {
    try {
      if (!id) throw new Error('ID de usuario no válido');
      const body = fechaBaja ? { fecha_baja: fechaBaja } : {};
      await api.post(`/usuarios/${id}/baja`, body, token, { usuario: userObj?.email || user });
      cargarUsuarios();
      cargarUsuariosGestion();
    } catch (err) {
      console.error('❌ Error dando de baja usuario:', err);
      throw err;
    }
  };

  const reactivarUsuario = async (id) => {
    try {
      if (!id) throw new Error('ID de usuario no valido');
      await api.post(`/usuarios/${id}/reactivar`, {}, token, { usuario: userObj?.email || user });
      cargarUsuarios();
      cargarUsuariosGestion();
    } catch (err) {
      console.error('Error reactivando usuario:', err);
      throw err;
    }
  };

  const editarUsuario = async (id, cambios) => {
    try {
      await api.put(`/usuarios/${id}`, cambios, token, { usuario: userObj?.email || user });
      cargarUsuarios();
      cargarUsuariosGestion();
    } catch (err) {
      console.error('Error editando usuario:', err);
      throw err;
    }
  };

  const reestablecerPassword = async (usuarioId) => {
    try {
      await api.post(`/usuarios/${usuarioId}/restablecer`, null, token, { usuario: userObj?.email || user });
      alert(`Nueva contraseña enviada`);
    } catch (err) {
      console.error('❌ Error reestableciendo contraseña:', err);
    }
  };

  // deep-link inicial
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = (params.get('tab') || '').toLowerCase();
    const vista = (params.get('vista') || '').toLowerCase();

    if (tab === 'usuarios') setActiveTab('Usuarios');
    else if (tab === 'logs') setActiveTab('Logs');
    else if (tab === 'ausencias') setActiveTab('Ausencias');
    else if (tab === 'saldos') setActiveTab('Saldos');
    else if (tab === 'informes') setActiveTab('Informes');
    else setActiveTab('Solicitudes');

    if (tab === 'solicitudes') {
      if (vista === 'aprobadas')
        setFiltroInicialSolicitudes({ vista: 'aprobadas', estado: 'aprobada', solo_pendientes: false });
      else if (vista === 'rechazadas')
        setFiltroInicialSolicitudes({ vista: 'rechazadas', estado: 'rechazada', solo_pendientes: false });
      else if (vista === 'historico' || vista === 'todas')
        setFiltroInicialSolicitudes({ vista: 'historico', estado: 'all', solo_pendientes: false });
      else setFiltroInicialSolicitudes({ vista: 'pendientes', estado: 'pendiente', solo_pendientes: true });
    }
  }, []);

  // reactiva con back/forward
  useEffect(() => {
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      const tab = (params.get('tab') || '').toLowerCase();
      const vista = (params.get('vista') || '').toLowerCase();

      if (tab === 'usuarios') setActiveTab('Usuarios');
      else if (tab === 'logs') setActiveTab('Logs');
      else if (tab === 'ausencias') setActiveTab('Ausencias');
      else if (tab === 'saldos') setActiveTab('Saldos');
      else if (tab === 'informes') setActiveTab('Informes');
      else setActiveTab('Solicitudes');

      if (tab === 'solicitudes') {
        if (vista === 'aprobadas')
          setFiltroInicialSolicitudes({ vista: 'aprobadas', estado: 'aprobada', solo_pendientes: false });
        else if (vista === 'rechazadas')
          setFiltroInicialSolicitudes({ vista: 'rechazadas', estado: 'rechazada', solo_pendientes: false });
        else if (vista === 'historico' || vista === 'todas')
          setFiltroInicialSolicitudes({ vista: 'historico', estado: 'all', solo_pendientes: false });
        else setFiltroInicialSolicitudes({ vista: 'pendientes', estado: 'pendiente', solo_pendientes: true });
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // carga inicial
  useEffect(() => {
    cargarSolicitudes();
    cargarAusencias();
    cargarLogs();
    cargarUsuarios();
    cargarUsuariosGestion();
  }, []);

  // KPIs
  const pendientes = useMemo(() => solicitudes.filter((s) => s.estado === 'pendiente').length, [solicitudes]);
  const aprobadas = useMemo(() => solicitudes.filter((s) => s.estado === 'aprobada').length, [solicitudes]);
  const rechazadas = useMemo(() => solicitudes.filter((s) => s.estado === 'rechazada').length, [solicitudes]);

  const abrirSolicitudes = (vista) => {
    setActiveTab('Solicitudes');
    if (vista === 'aprobadas')
      setFiltroInicialSolicitudes({ vista: 'aprobadas', estado: 'aprobada', solo_pendientes: false });
    else if (vista === 'rechazadas')
      setFiltroInicialSolicitudes({ vista: 'rechazadas', estado: 'rechazada', solo_pendientes: false });
    else if (vista === 'historico' || vista === 'todas')
      setFiltroInicialSolicitudes({ vista: 'historico', estado: 'all', solo_pendientes: false });
    else setFiltroInicialSolicitudes({ vista: 'pendientes', estado: 'pendiente', solo_pendientes: true });

    const url = `?tab=solicitudes&vista=${vista}`;
    window.history.pushState({}, '', url);
  };

  const vistaActiva = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const v = (params.get('vista') || filtroInicialSolicitudes?.vista || 'pendientes').toLowerCase();
    return v === 'historico' ? 'todas' : v;
  }, [filtroInicialSolicitudes]);

  const tabs = [
    {
      name: 'Solicitudes',
      label: 'Solicitudes',
      icon: '📝',
      content: <SolicitudesTab solicitudes={solicitudes} onResolver={resolver} filtroInicial={filtroInicialSolicitudes} />,
    },
    { name: 'Ausencias', label: 'Ausencias', icon: '📅', content: <GestionAusencias token={token} /> },
    { name: 'Saldos', label: 'Saldos', icon: '💼', content: <AdminSaldos token={token} /> },
    {
      name: 'Usuarios',
      label: 'Usuarios',
      icon: '👥',
      content: (
        <UsuariosTab
          usuarios={usuariosGestion}
          onCrearUsuario={crearUsuario}
          msg={msg}
          token={token}
          API_URL={API_URL}
          onDarBaja={darBajaUsuario}
          onReactivar={reactivarUsuario}
          onEditar={editarUsuario}
          onReestablecer={(u) => reestablecerPassword(u)}
        />
      ),
    },
    { name: 'Logs', label: 'Logs', icon: '📁', content: <LogsTab logs={logs} pausaMin={pausaMin} pausaEnabled={pausaEnabled} /> },
    { name: 'Informes', label: 'Informes', icon: '📊', content: <InformesPanel token={token} usuarios={usuarios} /> },
  ];

  const handleChangeTab = (tab) => {
    setActiveTab(tab.name);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab.name.toLowerCase());
    if (tab.name !== 'Solicitudes') params.delete('vista');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  };

  const tabActual = tabs.find((t) => t.name === activeTab)?.content;

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: "url('/fondo.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/10 to-transparent" />

      {/* Header con branding (props + rescate /auth/me) */}
      <Header
        usuario={user}
        brandDir={brandForHeader}
        companyName={companyForHeader}
        onLogout={onLogout}
        showSwitchVista={role === 'admin'}
        switchLabel="Vista empleado"
        switchToPath="/empleado"
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="rounded-3xl bg-white/55 backdrop-blur-xl border border-white/50 shadow-2xl shadow-black/5 p-5 sm:p-8 space-y-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-blue-900 tracking-tight drop-shadow-sm">
            ⚙️ Panel de Administración
          </h1>

          <TabGroup
            tabs={tabs}
            activeTab={tabs.find((t) => t.name === activeTab)}
            setActiveTab={handleChangeTab}
          />

          {activeTab === 'Solicitudes' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
              <div
                role="button"
                tabIndex={0}
                onClick={() => abrirSolicitudes('pendientes')}
                className="outline-none focus:ring-2 focus:ring-blue-300 rounded-2xl transition"
              >
                <KPICard
                  title="Pendientes"
                  value={pendientes}
                  icon="⏳"
                  active={vistaActiva === 'pendientes'}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => abrirSolicitudes('aprobadas')}
                className="outline-none focus:ring-2 focus:ring-blue-300 rounded-2xl transition"
              >
                <KPICard
                  title="Aprobadas"
                  value={aprobadas}
                  icon="✅"
                  active={vistaActiva === 'aprobadas'}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => abrirSolicitudes('rechazadas')}
                className="outline-none focus:ring-2 focus:ring-blue-300 rounded-2xl transition"
              >
                <KPICard
                  title="Rechazadas"
                  value={rechazadas}
                  icon="❌"
                  active={vistaActiva === 'rechazadas'}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => abrirSolicitudes('todas')}
                className="outline-none focus:ring-2 focus:ring-blue-300 rounded-2xl transition"
              >
                <KPICard
                  title="Todas las solicitudes"
                  value={solicitudes.length}
                  icon="📊"
                  active={vistaActiva === 'todas'}
                />
              </div>
            </div>
          )}

          <section className="mt-2">{tabActual}</section>
        </div>
      </main>
    </div>
  );
}
